import { useState, useCallback, useMemo } from "react";
import { UseFormWatch } from "react-hook-form";
import { ProviderState } from "../../../../../enums";
import { AvailableProvider, FormData } from "../types";
import type { ProviderData } from "../Components/Review";
import {
  getProviderCapabilities,
  getDefaultMethods,
} from "../utils/providerHelpers";

interface UseProviderReviewProps {
  selectedProvider: AvailableProvider | null;
  watch: UseFormWatch<FormData>;
}

export function useProviderReview({
  selectedProvider,
  watch,
}: UseProviderReviewProps) {
  const [showReview, setShowReview] = useState(false);

  const capabilities = useMemo(
    () => getProviderCapabilities(selectedProvider),
    [selectedProvider],
  );

  const getReviewProviderData = useCallback((): ProviderData | null => {
    const formData = watch();
    if (!selectedProvider) return null;

    const methods =
      capabilities.availableMethods.length > 0
        ? capabilities.availableMethods
        : getDefaultMethods(selectedProvider?.schema);

    const brands = capabilities.hasBrands ? formData.brands || [] : [];

    return {
      name: selectedProvider.name,
      code: selectedProvider.code || "",
      priority: formData.priority || 0,
      methods,
      credentials: formData.credentials || {},
      acquirer: {
        merchantId: formData.merchantId || "",
        brands,
      },
      state: formData.isActive ? ProviderState.Enabled : ProviderState.Disabled,
    };
  }, [
    selectedProvider,
    watch,
    capabilities.availableMethods,
    capabilities.hasBrands,
  ]);

  const handleReviewToEdit = useCallback(() => {
    setShowReview(false);
  }, []);

  return {
    showReview,
    setShowReview,
    getReviewProviderData,
    handleReviewToEdit,
  };
}
